import { PROPERTY_GRID_SUBJECT_PROPERTY, isUndefined, schSourceType } from '@aloft/toolkit-shared';
import { Block, Flex, Icon, Tooltip, Typography, theme } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import {
  Button,
  ButtonProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@aloft/toolkit-sui/chakra';

import {
  Controller,
  ControllerRenderProps,
  FieldError,
  FieldPath,
  useWatch,
} from 'react-hook-form';

import { useAppDispatch } from '../../../redux/hooks.ts';
import { PropertyGridFieldName, PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { editableFieldOutlineSx } from './consts.ts';
import { PropertyGridFieldProps } from './types.ts';

const IconElement = ({ isOpen, error }: { isOpen: boolean; error?: FieldError }) =>
  error ? (
    <Tooltip label={error.message}>
      <Icon icon="error-warning" color="alertError" />
    </Tooltip>
  ) : (
    <Icon icon={isOpen ? 'arrow-drop-up' : 'arrow-drop-down'} />
  );

export const EditableEnumField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
  setValue,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();

  const {
    fieldName: rawFieldName,
    options,
    includeEmptyOption,

    toDisplayValue,
    toValue,
  } = field;

  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const arvFieldName = rawFieldName.replace(
    /^editableData\./,
    'editableData.arv.',
  ) as PropertyGridFieldName;
  const fieldName = isSubject && isArv ? arvFieldName : rawFieldName;

  const propertyDataFieldName =
    `columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>;
  const selectedSourceFieldName =
    `columns.${columnIndex}.propertyData.selectedSources.${fieldName}.source` as FieldPath<PropertyGridForm>;

  const value = useWatch({ control, name: propertyDataFieldName }) as string | undefined;

  const handleSetFieldValue = (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string | number,
  ) => {
    if (isUndefined(newValue)) return;

    const parsedNewValue = toValue ? toValue(newValue.toString()) : (newValue as string);
    onChange(parsedNewValue);

    setValue(selectedSourceFieldName, schSourceType.enum.custom);
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  if (!options) return null;

  const sharedButtonStyle: Partial<ButtonProps> = {
    width: '100%',
    height: '32px',
    borderRadius: '0',
    color: theme.colors.textPrimary,
    padding: '4px 8px',
    fontWeight: 'normal',
    ...editableFieldOutlineSx,
    _active: {},
    _focus: {},
    _focusWithin: {},
    _hover: { backgroundColor: theme.colors.surface2 },
  };

  return (
    <Controller
      name={propertyDataFieldName}
      control={control}
      render={({ field: { onChange, ref }, fieldState: { error } }) => {
        const displayValue = toDisplayValue
          ? toDisplayValue(value as string | undefined)
          : (value as string | number | undefined);

        const matchingOption = options.find((opt) => opt.value === displayValue);

        return (
          <Popover gutter={-32} variant="inline" isLazy matchWidth>
            {({ isOpen, onClose }) => {
              const handleOnClose = () => {
                if (document.activeElement instanceof HTMLElement) document.activeElement?.blur();

                onClose();
              };

              let title: string | undefined;
              let backgroundColor = theme.colors.surface1;
              if (isUndefined(displayValue)) {
                title = 'This field is empty. Please input a value.';
                backgroundColor = theme.colors.selected4;
              }

              return (
                <>
                  <PopoverTrigger>
                    <Button
                      {...sharedButtonStyle}
                      ref={ref}
                      title={title}
                      backgroundColor={backgroundColor}
                      gridColumn={`span ${colSpan}`}
                      onClick={handleOnClose}
                    >
                      <Flex alignItems="center" justifyContent="space-between" width="100%">
                        <Block
                          __dangerDoNotUseSx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {matchingOption?.label}
                        </Block>
                        <IconElement isOpen={isOpen} error={error} />
                      </Flex>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent borderRadius="0" width="100%">
                    <Button
                      {...sharedButtonStyle}
                      alignItems="center"
                      justifyContent="space-between"
                      gap="4px"
                      borderWidth="0px"
                      borderStyle="solid"
                      borderColor="strokeDefault"
                      borderBottomWidth="1px"
                      backgroundColor="transparent"
                      onClick={handleOnClose}
                    >
                      <Typography variant="body1">{matchingOption?.label}</Typography>
                      <IconElement isOpen={isOpen} error={error} />
                    </Button>
                    <Flex flexDirection="column" gap="1px" maxHeight="25vh" overflowY="auto">
                      {includeEmptyOption && (
                        <Button
                          title="None"
                          backgroundColor={theme.colors.surface1}
                          {...sharedButtonStyle}
                          onClick={() => {
                            handleSetFieldValue(onChange, '');
                            handleOnClose();
                          }}
                        />
                      )}
                      {options.map((option) => (
                        <Button
                          key={option.value}
                          title={option.value.toString()}
                          backgroundColor={theme.colors.surface1}
                          {...sharedButtonStyle}
                          onClick={() => {
                            handleSetFieldValue(onChange, option.value);
                            handleOnClose();
                          }}
                        >
                          <Flex alignItems="center" justifyContent="space-between" width="100%">
                            <Block
                              __dangerDoNotUseSx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Typography variant="body1">{option.label}</Typography>
                            </Block>
                          </Flex>
                        </Button>
                      ))}
                    </Flex>
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
        );
      }}
    />
  );
};
